import { CanceledError } from 'axios'
import { makeAutoObservable, runInAction } from 'mobx'
import { debounce } from 'lodash'
import { logger, removeSpacesFromNumericString } from 'shared/lib'
import { RequestCancelAxios } from 'shared/api/lib'
import { type IContactAll } from 'entities/Contacts'
import { Contact } from 'entities/Contacts/model/Contact'
import { type IParamsContactsSearch, type IParamsCreateContact } from 'entities/Contacts/api/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { IntegrationKey } from 'entities/Integrations'
import { type ISearchDropdownStoreOptions } from 'widgets/SearchDropdown/store/types'

export class SearchDropdownContactsStore {
  private _data: IContactAll[] = []
  private _loading = false
  private _search = ''
  private _total = 0
  private _page = 1
  private _perPage = 3
  private _debounceFetch: ReturnType<typeof debounce>
  private _selectedItemsMap: Map<number, IContactAll> = new Map()
  private _options: ISearchDropdownStoreOptions<IContactAll> | null = null
  private _cancelRequest = new RequestCancelAxios()
  private _creating = false

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
    this._debounceFetch = debounce(this.init, 500)
  }

  get firstSelectedItemId() {
    const id = this._selectedItemsMap.entries().next().value?.[0]

    if (!id) return null

    return id
  }

  get selectedItems() {
    return Array.from(this._selectedItemsMap.values())
  }

  get hasSelectedItems() {
    return !!this.selectedItemsCount
  }

  get selectedItemsListIds() {
    return Array.from(this._selectedItemsMap).map((item) => item[1].id)
  }

  get selectedItemsCount() {
    return this._selectedItemsMap.size
  }

  get canSearch() {
    // Start search from 3 or more characters
    return this._search.length > 2
  }

  get hasItems() {
    return !!this._data.length
  }

  get first3Items() {
    return this._data.slice(0, 3)
  }

  initOptions = (options: ISearchDropdownStoreOptions<IContactAll>) => {
    this._options = options
  }

  initCancelTokenSource = () => {
    this._cancelRequest.init()
  }

  resetCancelTokenSource = () => {
    this._cancelRequest.reset()
  }

  isSelected = (id: number) => {
    return !!this._selectedItemsMap.get(id)
  }

  selectTypeItem = (item: IContactAll) => {
    this.toggleTypeItem(item)
    this._options?.resetSearch?.()
  }

  createContact = async (item: IContactAll, callback: (item: IContactAll) => void) => {
    if (this._creating) return
    if (!(item instanceof Contact)) {
      runInAction(() => {
        this._creating = true
      })
      const paramsCreateContact = {
        forLink: true,
        fromConversation: true,
      }

      if (item.phoneCount > 1) {
        this._entities.contactsStore.contactChoosePhoneModalStore.openModal({
          contact: item,
          callback: callback,
          params: paramsCreateContact,
        })

        runInAction(() => {
          this._creating = false
        })
        return
      }

      const params: IParamsCreateContact = {
        number: item.phone || item.mobile_phone,
        ...item.params,
        ...paramsCreateContact,
      }

      const integration = this._entities.integrationsStore.getIntegration(item.integration_type)

      params.integration_id = integration?.id

      const contact = await this._entities.contactsStore.createContact(params)

      if (contact) callback(contact)

      runInAction(() => {
        this._creating = false
      })

      return
    }

    callback(item)
  }

  resetType = () => {
    this._selectedItemsMap.clear()
    this.reset()
  }

  toggleTypeItem = (item: IContactAll) => {
    if (this._options?.selectedMode) {
      if (this._selectedItemsMap.has(item.id)) {
        this._selectedItemsMap.delete(item.id)
      } else {
        this._selectedItemsMap.set(item.id, item)
      }
    }

    this._options?.toggleTypeItem?.(item)
  }

  reset = () => {
    this._data = []
    this._loading = false
    this._search = ''
    this._total = 0
    this._page = 1
    this._cancelRequest.reset()
    this._debounceFetch.cancel()
  }

  handleSearch = (value: string, fetch = true) => {
    this._search = value
    this.resetCancelTokenSource()
    fetch && this._debounceFetch()
  }

  init = async () => {
    if (!this.canSearch) return

    try {
      runInAction(() => {
        this._loading = true
        this._data = []
        this._total = 0
        this.initCancelTokenSource()
      })

      const params: IParamsContactsSearch = {
        page: this._page,
        per_page: this._perPage,
        skip_opted_out: 0,
        term: this._search || '*',
        exclude: this.selectedItemsListIds,
      }

      if (this._search) params.term = removeSpacesFromNumericString(this._search)

      if (this._search && this._entities.organizationStore.isHipaaCompliant) {
        this._entities.organizationStore.handleTrackOrganizationActivity({
          event: 'phi.contact.searched',
          data: {
            search_query: this._search,
          },
        })
      }
      const requests: Promise<
        | {
            total: number
            contacts: IContactAll[]
          }
        | undefined
      >[] = [
        this._entities.contactsStore.getIntegrationSearchContact(
          IntegrationKey.salesmsg,
          params,
          this._cancelRequest.token
        ),
      ]

      this._entities.integrationsStore.connectedIntegrations.forEach((item) => {
        requests.push(
          this._entities.contactsStore.getIntegrationSearchContact(
            item.key,
            params,
            this._cancelRequest.token
          )
        )
      })

      const allData = await Promise.allSettled(requests)

      let total = 0
      let items: IContactAll[] = []

      allData.forEach((res) => {
        if (res.status === 'fulfilled') {
          if (!res.value || !res.value.total) return

          total += res.value.total
          items = [...items, ...res.value.contacts]
        }

        if (res.status === 'rejected') logger.error(res.reason)
      })

      runInAction(() => {
        this._data = items
        this._total = total
      })
    } catch (e) {
      if (e instanceof CanceledError) return

      logger.error(e)
    } finally {
      this._loading = false
    }
  }

  get search() {
    return this._search
  }

  get loading() {
    return this._loading
  }

  get options() {
    return this._options
  }

  get total() {
    return this._total
  }

  get selectedItemsMap() {
    return this._selectedItemsMap
  }

  get creating() {
    return this._creating
  }
}
